import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { useAuthStore } from '~/store/auth'

function routeNameStartsWith(route: RouteLocationNormalizedLoaded, prefix: string): boolean {
    return typeof route.name === 'string' && route.name.startsWith(prefix)
}

export default defineNuxtRouteMiddleware((to) => {
    const authStore = useAuthStore()

    const securedHomePage = 'dashboard'
    const authPage = 'auth'

    if (to.meta.allowAnonymous) {
        return
    }

    //Don´t do anything on this pages
    const exceptions: string[] = ['auth-activate', 'auth-social']

    // CMS Feature
    const cms = useFeature('cms') as boolean
    if (cms) exceptions.push('slug', 'news-slug')
    if (!cms && to.name === 'slug') return navigateTo(securedHomePage)

    const currentName: string | null = typeof to.name === 'string' ? to.name : null
    if (currentName && exceptions.some(ex => currentName.startsWith(ex))) {
        return
    }

    // If the user is not yet authenticated
    const notYetResolved = authStore.isAuthenticated == null
    if (notYetResolved && import.meta.client) {
        //////////////////////////////////////////////////////////////////////////////////////
        // FAST BOOT:
        // Will Speed Up Page Load
        // If user is authenticated via localstorage and has any ability
        // Make site already visible, will be maybe changed in the following request
        // Set abilities from localStorage, will be maybe changed in the following request
        //////////////////////////////////////////////////////////////////////////////////////
        const lsIsAuth = localStorage.getItem('isAuthenticated')
        const lsAbilities = localStorage.getItem('abilities')
        const lsUserData = localStorage.getItem('userData')

        if (lsIsAuth === 'true' && lsAbilities && lsAbilities !== 'undefined') {
            try { authStore.abilities = JSON.parse(lsAbilities) } catch { /* ignore */ }
            if (lsUserData && lsUserData !== 'undefined') {
                try { authStore.userData = JSON.parse(lsUserData) } catch { /* ignore */ }
            }

            if (routeNameStartsWith(to, 'auth')) {
                return navigateTo(securedHomePage)
            }
        } else {
            //////////////////////////////////////////////////////////////////////////////////////
            // If user is not authenticated via localstorage
            // Redirect User to Auth Page.
            //////////////////////////////////////////////////////////////////////////////////////
            if (!routeNameStartsWith(to, authPage)) {
                // save the current path to redirect after login
                authStore.redirectPathAfterLogin = to.fullPath
                return navigateTo('/' + authPage)
            }
        }

        //Now check via ajax if user is really authenticated
        authStore.getAuthData().then(() => {
            //And then redirect accordingly
            redirectRules(authStore, to, authPage, securedHomePage)
        })
    } else {
        redirectRules(authStore, to, authPage, securedHomePage)
    }
})

function redirectRules(
    authStore: ReturnType<typeof useAuthStore>,
    to: RouteLocationNormalizedLoaded,
    authPage: string,
    securedHomePage: string
) {
    if (to.meta.allowAnonymous) {
        return
    }

    if (!authStore.isAuthenticated && !routeNameStartsWith(to, 'auth')) {
        return navigateTo('/' + authPage)
    }

    if (authStore.isAuthenticated && (to.name == null || routeNameStartsWith(to, 'auth'))) {
        return navigateTo(securedHomePage)
    }
}
